import type { RefObject } from "react";
import { useState } from "react";
import type { HlsVideoElement } from "hls-video-element";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { MediaTranscoding } from "@circle-react/types/MediaTranscoding";
import { TranscriptNotification } from "@circle-react-uikit/TranscriptsPlayer/TranscriptNotification";
import { TranscriptPopup } from "@circle-react-uikit/TranscriptsPlayer/TranscriptPopup";
import { MediaSettingsOverlay } from "../MediaSettingsOverlay";
import { Player } from "./Player";
import { PlayerTheme } from "./PlayerTheme";
import { usePlayer } from "./hooks/usePlayer";
import { useVideoSource } from "./hooks/useVideoSource";
import { useVideoTranscript } from "./hooks/useVideoTranscript";
import type { VideoMetadata, VideoVariants } from "./types";

export interface VideoPlayerProps {
  chaptersUrl?: string;
  className?: string;
  contentType?: string;
  downloadable?: boolean;
  downloadUrl?: string;
  mediaTranscoding?: MediaTranscoding;
  mediaTranscriptId?: number;
  onLoadedMetadata?: (metadata: VideoMetadata) => void;
  onTimeUpdate?: (time: number) => void;
  posterUrl?: string;
  preload?: "none" | "metadata" | "auto";
  settingsEnabled?: boolean;
  signedId: string;
  src: string;
  variants?: VideoVariants;
}

interface RenderPlayerProps {
  videoRef: RefObject<HlsVideoElement>;
}

export const VideoPlayer = ({
  chaptersUrl,
  className,
  contentType,
  downloadable = false,
  downloadUrl,
  mediaTranscoding,
  mediaTranscriptId,
  onTimeUpdate,
  onLoadedMetadata,
  posterUrl,
  preload = "none",
  settingsEnabled = true,
  signedId,
  src,
  variants,
}: VideoPlayerProps) => {
  const { playbackSrc, isHls } = useVideoSource({
    mediaTranscoding,
    signedId,
    src,
    variants,
  });

  const {
    mediaTranscript,
    captionsUrl,
    cues,
    shouldDisplayTranscript,
    shouldDisplayNotification,
    updateMediaTranscript,
  } = useVideoTranscript({ mediaTranscriptId });

  const mainPlayer = usePlayer();
  const fullScreenPlayer = usePlayer();
  const [currentTime, setCurrentTime] = useState(0);

  const onFullscreenToggle = () => {
    // Stop the main player so its progress callbacks are not executed
    mainPlayer.pause();
    // Update the playback position to sync with the one in the transcript popup
    mainPlayer.seek(currentTime);
  };

  const handleTimeUpdate = (time: number) => {
    onTimeUpdate?.(time);
    setCurrentTime(time);
  };

  const renderPlayer = ({ videoRef }: RenderPlayerProps) => (
    <PlayerTheme downloadable={downloadable} downloadUrl={downloadUrl}>
      <Player
        className="max-h-full min-h-full min-w-full max-w-full object-contain object-center"
        src={playbackSrc}
        contentType={contentType}
        captionsUrl={captionsUrl}
        chaptersUrl={chaptersUrl}
        preload={preload}
        poster={posterUrl}
        isHls={isHls}
        signedId={signedId}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={onLoadedMetadata}
        videoRef={videoRef}
      />
    </PlayerTheme>
  );

  return (
    <div className={classNames("group relative flex flex-col", className)}>
      {renderPlayer(mainPlayer)}
      {settingsEnabled && <MediaSettingsOverlay signedId={signedId} />}
      {shouldDisplayTranscript && (
        <TranscriptPopup
          onSeek={mainPlayer.seek}
          renderFullscreenPlayer={() => renderPlayer(fullScreenPlayer)}
          onFullscreenSeek={fullScreenPlayer.seek}
          onFullscreenToggle={onFullscreenToggle}
          cues={cues}
          currentTime={currentTime}
        />
      )}
      {shouldDisplayNotification && (
        <TranscriptNotification
          url={src}
          onNewMediaTranscript={updateMediaTranscript}
          onMediaTranscriptUpdated={updateMediaTranscript}
          mediaTranscript={mediaTranscript}
          isLoading={false}
        />
      )}
    </div>
  );
};
